// Dữ liệu mẫu cho nhóm "Tổng chi phí marketing" khi server chưa có chiến dịch quảng cáo nào
// (chiến dịch chỉ có khi đồng bộ từ tài khoản quảng cáo, không tạo tay được).
// Chỉ bật khi chạy dev (npm start) / localhost, chỉ đọc — không ghi gì lên server.
import type { Dayjs } from 'dayjs';
import type { MarketingReportV2Data } from 'api/DashboardStatistics';

// Không xét theo API: bản v2 khách đang dùng cũng gọi API dev, không được hiện dữ liệu giả cho khách
export const SAMPLE_ALLOWED = process.env.NODE_ENV === 'development'
  || (typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname));
export const SAMPLE_KEY = 'dashboard-kinh-doanh.sample-mkt';

/** Mỗi chiến dịch theo ngày: ngân sách, % ngân sách đã tiêu, chi phí / tin nhắn, tỷ lệ tin nhắn → lead, doanh thu / lead */
const CAMPAIGNS: { id: string; name: string; channel: 'Facebook' | 'TikTok'; active: boolean; daily: number; used: number; perMsg: number; toLead: number; revPerLead: number }[] = [
  { id: 'sample-1', name: '[FB] Nâng mũi cấu trúc - Tin nhắn', channel: 'Facebook', active: true, daily: 1_200_000, used: 0.96, perMsg: 45_000, toLead: 0.18, revPerLead: 2_400_000 },
  { id: 'sample-2', name: '[FB] Cắt mí Hàn Quốc - Tin nhắn', channel: 'Facebook', active: true, daily: 900_000, used: 0.88, perMsg: 52_000, toLead: 0.15, revPerLead: 1_900_000 },
  { id: 'sample-3', name: '[FB] Tiêm filler môi - Ưu đãi tháng', channel: 'Facebook', active: true, daily: 600_000, used: 1.02, perMsg: 38_000, toLead: 0.22, revPerLead: 700_000 },
  { id: 'sample-4', name: '[FB] Remarketing khách cũ', channel: 'Facebook', active: false, daily: 300_000, used: 0.55, perMsg: 60_000, toLead: 0.25, revPerLead: 1_500_000 },
  { id: 'sample-5', name: '[TT] Trẻ hoá da - Video ngắn', channel: 'TikTok', active: true, daily: 700_000, used: 0.92, perMsg: 30_000, toLead: 0.09, revPerLead: 900_000 },
  { id: 'sample-6', name: '[TT] Thử nghiệm nội dung KOC', channel: 'TikTok', active: false, daily: 400_000, used: 0.6, perMsg: 80_000, toLead: 0, revPerLead: 0 },
];

/** Số liệu mẫu cho một kỳ: tỷ lệ theo số ngày, lệch nhẹ giữa các kỳ để có so sánh tăng / giảm */
export function sampleMarketing(start: Dayjs, end: Dayjs, real?: MarketingReportV2Data | null): MarketingReportV2Data {
  const days = Math.max(1, end.startOf('day').diff(start.startOf('day'), 'day') + 1);
  const f = 0.9 + ((start.date() * 7 + start.month() * 13) % 21) / 100;
  const campaigns = CAMPAIGNS.map((c, i) => {
    const budget = c.daily * days;
    const spent = Math.round(budget * Math.min(1.1, c.used * f));
    const messages = Math.round(spent / c.perMsg);
    const leads = Math.round(messages * c.toLead);
    const revenue = Math.round(leads * c.revPerLead);
    return {
      id: -(i + 1), fb_campaign_id: c.id, name: c.name, channel: c.channel,
      status: c.active ? 'ACTIVE' : 'PAUSED', effective_status: c.active ? 'ACTIVE' : 'PAUSED',
      budget, budget_formatted: '', spent, spent_formatted: '', messages, leads,
      cost_per_message: messages ? Math.round(spent / messages) : 0, cost_per_message_formatted: '',
      revenue, revenue_formatted: '', ad_sets_count: 2, ads_count: 4, badges: [], badge_text: '',
    };
  });
  const sum = (k: 'budget' | 'spent' | 'messages' | 'leads' | 'revenue', ch?: string) => campaigns.filter(c => !ch || c.channel === ch).reduce((t, c) => t + c[k], 0);
  const channel = (ch: string) => ({ name: ch, spent: sum('spent', ch), messages: sum('messages', ch), leads: sum('leads', ch), revenue: sum('revenue', ch), campaign_count: campaigns.filter(c => c.channel === ch).length, is_integrated: true });
  const spent = sum('spent');
  const messages = sum('messages');
  const leads = sum('leads');
  const revenue = sum('revenue');
  const organic = Math.round(messages * 0.6);
  return {
    staff_response_times: real?.staff_response_times || [],
    summary: {
      total_spent: spent, total_budget: sum('budget'), ad_messages: messages, organic_messages: organic, total_messages: messages + organic,
      total_comments: Math.round(messages * 0.3), total_leads: leads,
      cost_per_message: messages ? Math.round(spent / messages) : 0, cost_per_lead: leads ? Math.round(spent / leads) : 0,
      total_revenue: revenue, roas: spent ? revenue / spent : 0,
    },
    campaigns,
    channel_breakdown: { spend_and_performance: { facebook: channel('Facebook'), tiktok: channel('TikTok') } },
  };
}
